<template>
  <!-- Wide mode: inline-expanding container styled per Figma "Overflow Container" -->
  <div
    v-if="!narrow"
    class="bui-nav-item-dd"
    :class="{ open: isOpen, 'has-active': hasActiveChild }"
  >
    <div class="dd-wide-row">
      <button
        type="button"
        class="trigger-main"
        :data-testid="triggerTestId"
        :aria-expanded="isOpen"
        :aria-controls="panelId"
        @click="toggle"
      >
        <NavIco>
          <slot name="icon" />
        </NavIco>
        <span class="label">{{ label }}</span>
      </button>
      <button
        type="button"
        class="caret-trigger"
        tabindex="-1"
        :data-testid="caretTestId"
        :aria-label="`${isOpen ? 'Close' : 'Open'} ${label} sub-menu`"
        @click="toggle"
      >
        <NavIco>
          <span class="caret" :class="{ flipped: isOpen }">
            <PhCaretUpDown />
          </span>
        </NavIco>
      </button>
    </div>
    <div
      v-show="isOpen"
      :id="panelId"
      class="panel"
      role="group"
      :aria-label="`${label} sub-menu`"
    >
      <slot />
    </div>
  </div>

  <!-- Narrow mode: hover/focus popover anchored to the right of the rail -->
  <div
    v-else
    class="bui-nav-item-dd narrow"
    @mouseenter="openPanel"
    @mouseleave="scheduleClose"
    @focusin="openPanel"
    @focusout="onFocusOut"
  >
    <button
      type="button"
      class="trigger narrow"
      :data-testid="triggerTestId"
      :aria-expanded="isOpen"
      :aria-haspopup="true"
      :title="label"
      @click="toggle"
    >
      <NavIco>
        <slot name="icon" />
      </NavIco>
    </button>
    <div
      v-show="isOpen"
      class="popover"
      role="menu"
      :aria-label="`${label} sub-menu`"
      @mouseenter="openPanel"
      @mouseleave="scheduleClose"
    >
      <div class="popover-title">{{ label }}</div>
      <slot />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import NavIco from '../../atoms/NavIco/NavIco.vue';
import { PhCaretUpDown } from '@phosphor-icons/vue';

let _ddSeq = 0;
const nextDdId = () => `bui-nav-item-dd-${++_ddSeq}`;

const props = withDefaults(
  defineProps<{
    label: string;
    narrow?: boolean;
    /** When any child route is active, force-open in wide mode */
    childPaths?: string[];
    /** Controlled open state, optional */
    open?: boolean | null;
  }>(),
  { narrow: false, open: null },
);

const emit = defineEmits<{ 'update:open': [value: boolean] }>();

const route = useRoute();
const internalOpen = ref(false);
/** When true, user explicitly collapsed while on an active child route. */
const userCollapsed = ref(false);
const panelId = nextDdId();

const triggerTestId = computed(() => {
  const slug = props.label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return slug ? `nav-${slug}` : undefined;
});

const caretTestId = computed(() =>
  triggerTestId.value ? `${triggerTestId.value}-toggle` : undefined,
);

const hasActiveChild = computed(() => {
  if (!props.childPaths?.length) return false;
  return props.childPaths.some(
    (p) => route.path === p || route.path.startsWith(p + '/'),
  );
});

const isOpen = computed(() => {
  if (props.open !== null) return !!props.open;
  if (userCollapsed.value) return false;
  if (!props.narrow && hasActiveChild.value) return true;
  return internalOpen.value;
});

function setOpen(v: boolean) {
  internalOpen.value = v;
  emit('update:open', v);
}
function toggle() {
  const next = !isOpen.value;
  if (!next && hasActiveChild.value) {
    userCollapsed.value = true;
  } else {
    userCollapsed.value = false;
  }
  setOpen(next);
}

let closeTimer: ReturnType<typeof setTimeout> | null = null;
function openPanel() {
  if (closeTimer) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }
  setOpen(true);
}

function scheduleClose() {
  if (closeTimer) clearTimeout(closeTimer);
  closeTimer = setTimeout(() => setOpen(false), 150);
}
function onFocusOut(e: FocusEvent) {
  const next = e.relatedTarget as Node | null;
  const root = (e.currentTarget as HTMLElement | null);
  if (!root || !next || !root.contains(next)) setOpen(false);
}

// Auto-close narrow popover on route change; re-allow auto-open for active child.
watch(() => route.path, () => {
  userCollapsed.value = false;
  if (props.narrow) setOpen(false);
});
</script>

<style lang="scss" scoped>
.bui-nav-item-dd {
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  z-index: 0;

  &.open {
    z-index: 1000;
  }

  .dd-wide-row {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    min-width: 0;
  }

  .trigger-main {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1 1 auto;
    min-width: 0;
    padding: var(--nav-padding-box, 8px);
    background: transparent;
    border: 0;
    border-radius: var(--nav-radius-box, 12px);
    color: var(--nav-label-primary, #f6f7ff);
    cursor: pointer;
    font-family: inherit;
    font-size: var(--type-body-lg-size, 20px);
    line-height: var(--type-body-lg-line-height, 23px);
    text-align: left;

    &:hover {
      background: var(--nav-btn-hover, #8e3fbc);
    }

    &:focus-visible {
      outline: 2px solid var(--nav-label-primary, #f6f7ff);
      outline-offset: 2px;
    }

    .label {
      flex: 1 0 0;
      min-width: 0;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow-x: hidden;
      overflow-y: visible;
    }
  }

  /* Matches NavItemExtra `.side-action` (logout control). */
  .caret-trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border: 0;
    background: transparent;
    color: var(--nav-label-primary, #f6f7ff);
    padding: var(--nav-padding-box, 8px);
    border-radius: var(--nav-radius-box, 12px);
    cursor: pointer;

    &:hover {
      background: var(--nav-btn-hover, #8e3fbc);
    }

    &:focus-visible {
      outline: 2px solid var(--nav-label-primary, #f6f7ff);
      outline-offset: 2px;
    }

    /* Same structure as NavItemExtra `.side-action` > NavIco (40px slot + icon padding). */
    .caret {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      line-height: 0;
      transition: transform 150ms ease;

      :deep(svg) {
        display: block;
        flex-shrink: 0;
      }

      &.flipped {
        transform: rotate(180deg);
      }
    }
  }

  &.has-active .trigger-main {
    background: var(--nav-btn-active, #a420c8);
  }

  .panel {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 100%;
    margin-bottom: 4px;
    background: var(--nav-fill-2, #4b1886);
    border-radius: var(--nav-radius-overflow, 24px);
    padding: 8px;
    box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.28);
    display: flex;
    flex-direction: column;
    gap: 6px;
    box-sizing: border-box;
  }
}

// Narrow / popover
.bui-nav-item-dd.narrow {
  position: relative;
  width: 56px;
  align-items: center;

  .trigger.narrow {
    width: 56px;
    padding: var(--nav-padding-box, 8px) 0;
    justify-content: center;
    gap: 0;
  }

  .popover {
    position: absolute;
    left: calc(100% + 8px);
    bottom: 100%;
    margin-bottom: 4px;
    top: auto;
    min-width: 0;
    width: max-content;
    max-width: var(--nav-dd-popover-max-width, 220px);
    box-sizing: border-box;
    background: var(--nav-fill-2, #4b1886);
    border-radius: var(--nav-radius-overflow, 24px);
    padding: 8px;
    box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.28);
    display: flex;
    flex-direction: column;
    gap: 6px;
    z-index: 1000;

    .popover-title {
      font-size: var(--type-body-lg-size, 20px);
      line-height: var(--type-body-lg-line-height, 23px);
      color: var(--nav-label-primary, #f6f7ff);
      padding: 4px 12px;
      opacity: 0.7;
    }

    :deep(.bui-nav-item) {
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
    }
  }
}
</style>
